import React, { useMemo } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useCollapsedMonths } from '../viewServices/useCollapsedMonths';
import EventCard from './EventCard';
import { eventStartKey } from '../../tab_calendar/viewServices/calendar_grid_utils';

const PlannerEventsList = ({
  events,
  onEventClick,
  fadePastEvents = true,
  showWeekday = true,
  showHour = false,
  timeFormatPreference,
}) => {
  const { collapsedMonths, toggleCollapse } = useCollapsedMonths();

  // Group events by year, then by month within each year
  const groupedByYear = useMemo(() => {
    const yearGroups = {};
    events.forEach((event) => {
      const startKey = eventStartKey(event);
      if (!startKey) return;

      const [year, monthNumber] = startKey.split('-').map(Number);
      const month = monthNumber - 1;
      const monthKey = `${year}-${month}`;
      const monthDate = new Date(year, month, 1);
      
      if (!yearGroups[year]) {
        yearGroups[year] = {};
      }
      
      if (!yearGroups[year][monthKey]) {
        yearGroups[year][monthKey] = {
          year,
          month,
          monthName: monthDate.toLocaleDateString('en-US', { month: 'long' }),
          events: []
        };
      }
      yearGroups[year][monthKey].events.push(event);
    });
    
    // Convert to array structure with sorted years and months (ascending - oldest first)
    const years = Object.keys(yearGroups).sort((a, b) => Number(a) - Number(b)); // Ascending
    return years.map(year => ({
      year: Number(year),
      months: Object.keys(yearGroups[year])
        .sort((a, b) => {
          const [yearA, monthA] = a.split('-').map(Number);
          const [yearB, monthB] = b.split('-').map(Number);
          if (yearA !== yearB) return yearA - yearB;
          return monthA - monthB; // Ascending month
        })
        .map(monthKey => ({
          key: monthKey,
          ...yearGroups[year][monthKey]
        }))
    }));
  }, [events]);

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-gray-400">No events yet. Create your first event!</p>
      </div>
    );
  }

  if (groupedByYear.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-gray-400">
          Events could not be grouped (needs start_datetime or start_date).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedByYear.map(({ year, months }) => (
        <div key={year} className="mb-4">
          {/* Year Header - same layout as Travel trips */}
          <div className="flex items-center mb-3 px-2">
            <div className="flex-1 h-px bg-gray-200"></div>
            <div className="px-3 py-1 bg-gray-100 rounded-full">
              <span className="text-sm font-medium text-gray-600">{year}</span>
            </div>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          
          {/* Months for this year */}
          <div className="space-y-2">
            {months.map(({ key, monthName, events: monthEvents }) => {
              const isCollapsed = collapsedMonths[key] ?? true; // Default collapsed
              
              return (
                <div key={key} className="bg-white rounded-lg shadow-sm">
                  {/* Month Header - more discreet */}
                  <button
                    onClick={() => toggleCollapse(key)}
                    className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 transition-colors"
                  >
                    <h2 className="text-sm font-medium text-gray-600">{monthName}</h2>
                    {isCollapsed ? (
                      <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  
                  {/* Month Events */}
                  {!isCollapsed && (
                    <div className="px-2 pb-2">
                      {monthEvents.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          onClick={() => onEventClick(event)}
                          fadePast={fadePastEvents}
                          showWeekday={showWeekday}
                          showHour={showHour}
                          timeFormatPreference={timeFormatPreference}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlannerEventsList;
